import { easeOutCubic, lerp } from "../utils";

export type StatsLineChartDataKey =
  | "points"
  | "assists"
  | "rebounds"
  | "steals"
  | "blocks";

const CHART_SVG_OFFSET_END_X = 88;
const CHART_SVG_OFFSET_START_X = 64;
const CHART_COLUMN_MIN_WIDTH = 80;

const CHART_HEIGHT = 220;

const ANIMATION_DURATION = 440;
const HOVER_ANIMATION_DURATION = 640;

const MIN_GRID_LINES_PER_GAME = 4;
const BASE_GRID_LINES_COLUMN_SPACE = 40;

export type StatsLineItem = {
  team: string;
  date: string;
  dateShort: string;
  gameData?: Record<StatsLineChartDataKey, number>;
};

type StatsLineChartProps = {
  container: HTMLDivElement;
  data: StatsLineItem[];
  dataKey?: StatsLineChartDataKey;
  dataKeyAbbreviation: Record<StatsLineChartDataKey, string>;
  onChartContainerScroll?: () => void;
};

export class StatsLineChart {
  lineChartContainer: HTMLElement;
  lineChart: HTMLDivElement;
  tooltip: HTMLDivElement;
  data: StatsLineItem[];
  onChartContainerScroll?: () => void;

  dataKeyAbbreviation: Record<StatsLineChartDataKey, string>;

  currDataKey: StatsLineChartDataKey;

  maxDataValue = 4;

  chartPoints: [number, number][] = [];

  chartWidth = 0;
  currentHoverIndex = 0;

  chartSVGMinWidth = 0;

  totalGames = 0;

  hoverPoint: [number, number] = [0, 0];

  animationStartChartPoints: [number, number][] = [];
  targetChartPoints: [number, number][] = [];

  hoverAnimationStartHoverPoint: [number, number] = [0, 0];
  hoverAnimationTargetHoverPoint: [number, number] = [0, 0];

  lineChartContainerResizeObserver: ResizeObserver;

  animationsFrameId?: number;
  animationStartTime = 0;

  hoverAnimationFrameId?: number;
  hoverAnimationStartTime = 0;

  hasAddedMouseEventListeners = false;

  prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches;

  constructor({
    container,
    data,
    dataKey,
    dataKeyAbbreviation,
    onChartContainerScroll,
  }: StatsLineChartProps) {
    this.lineChartContainer = container;
    this.data = data;
    this.currDataKey = dataKey || "points";

    this.dataKeyAbbreviation = dataKeyAbbreviation;

    this.onChartContainerScroll = onChartContainerScroll;

    const lineChartTemplate = document.querySelector(
      "#stats-line-chart"
    ) as HTMLTemplateElement;

    this.lineChart = (lineChartTemplate.content.cloneNode(true) as HTMLElement)
      .children[0] as HTMLDivElement;

    this.tooltip = this.lineChart.querySelector(
      "#stats-line-chart-tooltip"
    ) as HTMLDivElement;

    this.lineChartContainerResizeObserver = new ResizeObserver(
      this.onContainerResize
    );

    this.init({});
  }

  init = ({
    updateXaxis,
    updateYaxis,
  }: {
    updateXaxis?: boolean;
    updateYaxis?: boolean;
  }) => {
    this.lineChartContainer.appendChild(this.lineChart);

    this.totalGames = this.data.length;

    this.chartSVGMinWidth = this.totalGames * CHART_COLUMN_MIN_WIDTH;

    this.chartWidth = Math.max(
      this.lineChartContainer.getBoundingClientRect().width -
        CHART_SVG_OFFSET_END_X,
      this.chartSVGMinWidth
    );

    this.lineChart.style.setProperty("--chart-width", `${this.chartWidth}px`);

    this.currentHoverIndex = this.data.findIndex(
      (_, index) => !this.data[index + 1]?.gameData
    );

    this.setAxisLabels({
      isUpdateXaxis: updateXaxis,
      isUpdateYaxis: updateYaxis,
    });

    this.updateChartPoints();

    this.lineChartContainerResizeObserver.observe(this.lineChartContainer);

    const polylineSVG = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-chart"
    ) as SVGSVGElement;

    if (this.chartPoints.length && !this.hasAddedMouseEventListeners) {
      polylineSVG.addEventListener("mouseenter", this.onLineChartMouseMove);
      polylineSVG.addEventListener("mousemove", this.onLineChartMouseMove);
      polylineSVG.addEventListener("mouseleave", this.onLineChartMouseLeave);

      this.hasAddedMouseEventListeners = true;
    }

    const dataContainer = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-container"
    ) as HTMLDivElement;

    const lastGameWithDataIndex = this.chartPoints.length - 1;

    if (lastGameWithDataIndex >= 0) {
      const lastGameWithDataLabel = dataContainer.querySelectorAll(
        ".stats-line-chart__x-axis-label-value"
      )[lastGameWithDataIndex] as HTMLSpanElement;

      const dataContainerScrollX =
        lastGameWithDataLabel.offsetLeft -
        dataContainer.getBoundingClientRect().width / 2 +
        lastGameWithDataLabel.getBoundingClientRect().width;

      dataContainer.scrollLeft = dataContainerScrollX;

      const crosshair = polylineSVG.querySelector(
        "#stats-line-chart-data-crosshair"
      ) as SVGGElement;

      crosshair.style.removeProperty("display");

      const gridLineActiveLine = this.lineChartContainer.querySelector(
        "#stats-line-chart-gridlines-active-line"
      ) as SVGLineElement;

      gridLineActiveLine.style.removeProperty("display");

      this.tooltip.style.removeProperty("display");
    }

    if (this.onChartContainerScroll) {
      dataContainer.addEventListener("scroll", this.onChartContainerScroll);
    }
  };

  onLineChartMouseMove = (e: MouseEvent) => {
    const stepX = this.chartWidth / (this.totalGames - 1 || 1);
    const hoverIndex = Math.round(e.offsetX / stepX);

    const nextHoverIndex = Math.min(hoverIndex, this.chartPoints.length - 1);

    if (this.currentHoverIndex === nextHoverIndex) {
      return;
    }

    this.currentHoverIndex = nextHoverIndex;

    this.hoverAnimationStartHoverPoint = this.hoverPoint;
    this.hoverAnimationTargetHoverPoint =
      this.chartPoints[this.currentHoverIndex];

    if (this.hoverAnimationFrameId) {
      cancelAnimationFrame(this.hoverAnimationFrameId);
    }

    this.hoverAnimationStartTime = performance.now();
    this.hoverAnimationFrameId = requestAnimationFrame(this.animateHoverData);
  };

  onLineChartMouseLeave = () => {
    this.currentHoverIndex = this.chartPoints.length - 1;

    this.hoverAnimationStartHoverPoint = this.hoverPoint;
    this.hoverAnimationTargetHoverPoint =
      this.chartPoints[this.currentHoverIndex];

    if (this.hoverAnimationFrameId) {
      cancelAnimationFrame(this.hoverAnimationFrameId);
    }

    this.hoverAnimationStartTime = performance.now();
    this.hoverAnimationFrameId = requestAnimationFrame(this.animateHoverData);
  };

  setAxisLabels = ({
    isUpdateXaxis,
    isUpdateYaxis,
  }: {
    isUpdateXaxis?: boolean;
    isUpdateYaxis?: boolean;
  }) => {
    const maxValue = Math.max(
      1,
      ...(this.data
        .map((item) => item.gameData?.[this.currDataKey] || 0)
        .filter((value) => value !== undefined) as number[])
    );

    const stepGap = Math.ceil(maxValue / 4);

    this.maxDataValue = stepGap * 4;

    const yAxisLabels = this.lineChart.querySelectorAll(
      ".stats-line-chart__y-axis-label"
    );

    const totalLabels = yAxisLabels.length;

    if (isUpdateYaxis) {
      const yAxisLabelsContainer = this.lineChart.querySelector(
        "#stats-line-y-axis-labels"
      ) as HTMLDivElement;

      yAxisLabelsContainer.classList.add("stats-line-chart__y-axis--updating");

      requestAnimationFrame(() => {
        yAxisLabelsContainer.classList.remove(
          "stats-line-chart__y-axis--updating"
        );
      });
    }

    yAxisLabels.forEach((label, index) => {
      const value = ((totalLabels - index) * stepGap).toString();

      if (isUpdateYaxis && label.textContent) {
        const lastLabelElement = label.lastChild as HTMLSpanElement;
        lastLabelElement.setAttribute("aria-hidden", "true");
        const nextLabelElement = lastLabelElement.cloneNode(
          true
        ) as HTMLSpanElement;
        nextLabelElement.removeAttribute("aria-hidden");

        nextLabelElement.textContent = value;

        label.innerHTML = "";

        label.appendChild(lastLabelElement);
        label.appendChild(nextLabelElement);

        return;
      }

      label.innerHTML = `<span>${value}</span>`;
    });

    const xAxisLabels = this.lineChart.querySelector(
      "#stats-line-x-axis-labels"
    ) as HTMLDivElement;

    if (isUpdateXaxis) {
      xAxisLabels.classList.add("stats-line-chart__x-axis-labels--updating");

      requestAnimationFrame(() => {
        xAxisLabels.classList.remove(
          "stats-line-chart__x-axis-labels--updating"
        );
      });
    }

    xAxisLabels.innerHTML = "";

    xAxisLabels.style.setProperty("--total-items", this.totalGames.toString());

    this.data.forEach((statData) => {
      const label = document.createElement("span");

      const labelInnerElement = document.createElement("span");

      labelInnerElement.className = "stats-line-chart__x-axis-label-value";
      labelInnerElement.classList.toggle(
        "stats-line-chart__x-axis-label-value--future",
        !statData.gameData
      );

      labelInnerElement.textContent = statData.dateShort;

      label.appendChild(labelInnerElement);

      xAxisLabels.appendChild(label);
    });
  };

  setGridLinesDashes = () => {
    const gridLinesSVG = this.lineChartContainer.querySelector(
      "#stats-line-chart-gridlines"
    ) as SVGSVGElement;

    if (!gridLinesSVG) {
      return;
    }

    const height = 165;

    gridLinesSVG.setAttribute("width", this.chartWidth.toString());
    gridLinesSVG.setAttribute("viewBox", `0 0 ${this.chartWidth} 165`);

    const gridLineMask = gridLinesSVG.querySelector(
      "#stats-line-chart-gridlines-mask"
    ) as SVGMaskElement;

    gridLineMask.innerHTML = "";

    const lineWidth = 1;
    const spaceBetweenGames =
      (this.chartWidth - lineWidth) / (this.totalGames - 1);
    const linesPerGame = Math.max(
      MIN_GRID_LINES_PER_GAME,
      Math.floor(spaceBetweenGames / BASE_GRID_LINES_COLUMN_SPACE)
    );
    const numberOfLines = (this.totalGames - 1) * linesPerGame + 1;
    const columnSpaceBetweenLines =
      (this.chartWidth - lineWidth) / (numberOfLines - 1);

    const numberOfRows = 7;
    const dashHeight = 0.1;
    const totalDashGap = height - numberOfRows * dashHeight;
    const dashGap = totalDashGap / (numberOfRows - 1);

    for (let i = 0; i < numberOfLines; i++) {
      const line = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      const x = i === 0 ? 0 : i * columnSpaceBetweenLines + lineWidth;

      line.setAttribute("x1", x.toString());
      line.setAttribute("y1", "0");
      line.setAttribute("x2", x.toString());
      line.setAttribute("y2", height.toString());
      line.setAttribute("stroke", "#FFFFFF");
      line.setAttribute("stroke-linecap", "square");
      line.setAttribute("stroke-dasharray", `0.1 ${dashGap}`);

      gridLineMask.appendChild(line);
    }

    const gridLinesStickyLine = this.lineChartContainer.querySelector(
      "#stats-line-chart-gridlines-sticky-line"
    ) as SVGSVGElement;

    gridLinesStickyLine.setAttribute("y2", height.toString());
    gridLinesStickyLine?.setAttribute("stroke-dasharray", `0.1 ${dashGap}`);

    const gridLineActiveLine = gridLinesSVG.querySelector(
      "#stats-line-chart-gridlines-active-line"
    ) as SVGLineElement;

    gridLineActiveLine.setAttribute("y2", height.toString());
    gridLineActiveLine.setAttribute("stroke-dasharray", `0.1 ${dashGap}`);

    this.setActiveGridBar();
  };

  setActiveGridBar = () => {
    const gridLineActiveLine = this.lineChartContainer.querySelector(
      "#stats-line-chart-gridlines-active-line"
    ) as SVGLineElement;

    if (!this.chartPoints.length) {
      gridLineActiveLine?.style.setProperty("display", "none");

      return;
    }

    gridLineActiveLine?.style.setProperty(
      "transform",
      `translateX(${this.hoverPoint[0]}px)`
    );
  };

  updateChartPoints = () => {
    const chartPoints: [number, number][] = [];

    const stepX = this.chartWidth / (this.totalGames - 1 || 1);

    this.data.forEach((item, index) => {
      const gameData = item.gameData;

      if (!gameData) {
        return;
      }

      const x = index * stepX;
      const y =
        CHART_HEIGHT -
        (gameData[this.currDataKey] / (this.maxDataValue || 1)) * CHART_HEIGHT;

      chartPoints.push([x, y]);
    });

    if (this.chartPoints.length) {
      this.animationStartChartPoints = this.chartPoints;
      this.targetChartPoints = chartPoints;

      if (this.animationsFrameId) {
        cancelAnimationFrame(this.animationsFrameId);
      }

      this.animationStartTime = performance.now();
      this.animationsFrameId = requestAnimationFrame(this.animateChartPoints);
    } else {
      this.chartPoints = chartPoints;
      this.hoverPoint = this.chartPoints.at(-1)!;

      this.setDataPolyline();
      this.setDataGradient();
      this.setGridLinesDashes();
      this.setTooltip();
    }
  };

  animateChartPoints = (timestamp: number) => {
    const elapsed = timestamp - this.animationStartTime;

    const animationDuration = this.prefersReducedMotion
      ? 10
      : ANIMATION_DURATION;

    const relativeProgress = Math.min(elapsed / animationDuration, 1);
    const easedProgress = easeOutCubic(relativeProgress);

    this.chartPoints = this.animationStartChartPoints.map(
      (startPoint, index) => {
        const targetPoint = this.targetChartPoints[index];

        const x = lerp(startPoint[0], targetPoint[0], easedProgress);
        const y = lerp(startPoint[1], targetPoint[1], easedProgress);

        return [x, y] as [number, number];
      }
    );

    this.hoverPoint = this.chartPoints.at(-1)!;

    this.setDataPolyline();
    this.setDataGradient();
    this.setGridLinesDashes();
    this.setTooltip();

    if (easedProgress < 1) {
      this.animationsFrameId = requestAnimationFrame(this.animateChartPoints);
    }
  };

  animateHoverData = (timestamp: number) => {
    if (
      !this.hoverAnimationStartHoverPoint ||
      !this.hoverAnimationTargetHoverPoint
    ) {
      return;
    }

    const elapsed = timestamp - this.hoverAnimationStartTime;

    const animationDuration = this.prefersReducedMotion
      ? 10
      : HOVER_ANIMATION_DURATION;

    const relativeProgress = Math.min(elapsed / animationDuration, 1);
    const easedProgress = easeOutCubic(relativeProgress);

    const hoverPointX = lerp(
      this.hoverAnimationStartHoverPoint[0],
      this.hoverAnimationTargetHoverPoint[0],
      easedProgress
    );

    const hoverPointY = lerp(
      this.hoverAnimationStartHoverPoint[1],
      this.hoverAnimationTargetHoverPoint[1],
      easedProgress
    );

    this.hoverPoint = [Math.max(hoverPointX, 0), hoverPointY];

    this.setActiveGridBar();
    this.setTooltip();
    this.setDataGradient();

    if (easedProgress < 1) {
      this.hoverAnimationFrameId = requestAnimationFrame(this.animateHoverData);
    }
  };

  setDataPolyline = () => {
    const polylineSVG = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-chart"
    ) as SVGSVGElement;

    if (!polylineSVG) {
      return;
    }

    polylineSVG.setAttribute("width", this.chartWidth.toString());
    polylineSVG.setAttribute(
      "viewBox",
      `0 0 ${this.chartWidth} ${CHART_HEIGHT}`
    );

    const polyline = polylineSVG.querySelector(
      "polyline"
    ) as SVGPolylineElement;

    polyline.setAttribute("points", this.chartPoints.join(" "));

    const crosshair = polylineSVG.querySelector(
      "#stats-line-chart-data-crosshair"
    ) as SVGGElement;

    if (this.chartPoints.length) {
      crosshair.setAttribute(
        "transform",
        `translate(${this.chartPoints.at(-1)![0]} ${
          this.chartPoints.at(-1)![1]
        })`
      );
    } else {
      crosshair.style.setProperty("display", `none`);
    }
  };

  setDataGradient = () => {
    const polygon = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-gradient"
    ) as SVGPolygonElement;

    if (!polygon) {
      return;
    }

    if (!this.chartPoints.length) {
      polygon.setAttribute("points", "");

      return;
    }

    const stepX = this.chartWidth / (this.totalGames - 1 || 1);

    const polygonPoints = [
      ...this.chartPoints,
      [this.chartPoints.at(-1)![0], CHART_HEIGHT],
      [0, CHART_HEIGHT],
    ];

    polygon.setAttribute("points", polygonPoints.join(" "));

    const polygonGradient = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-radial-gradient"
    ) as SVGRadialGradientElement;

    polygonGradient.setAttribute("cx", this.hoverPoint[0].toString());
    polygonGradient.setAttribute("cy", "0");
    polygonGradient.setAttribute("r", (stepX * 3).toString());

    const gradientMaskRect = this.lineChartContainer.querySelector(
      "#stats-line-chart-data-gradient-mask rect"
    ) as SVGRectElement;

    gradientMaskRect.setAttribute("width", this.hoverPoint[0].toString());
  };

  setTooltip = () => {
    const tooltipDataElements = this.tooltip.querySelectorAll("span");

    if (this.currentHoverIndex < 0) {
      this.tooltip.style.setProperty("display", "none");

      return;
    }

    const currentGameData = this.data[this.currentHoverIndex].gameData;

    if (!currentGameData) {
      this.tooltip.style.setProperty("display", "none");

      return;
    }

    const toolTipX = this.hoverPoint[0] + CHART_SVG_OFFSET_START_X;
    const toolTipY = this.hoverPoint[1];

    this.tooltip.style.setProperty(
      "transform",
      `translate(${toolTipX}px, ${toolTipY}px)`
    );

    tooltipDataElements[0].textContent = `${
      currentGameData[this.currDataKey]
    } ${this.dataKeyAbbreviation[this.currDataKey]}`;
    tooltipDataElements[1].textContent = this.data[this.currentHoverIndex].date;
    tooltipDataElements[2].textContent = `VS. ${
      this.data[this.currentHoverIndex].team
    }`;
  };

  onContainerResize = () => {
    this.chartWidth = Math.max(
      this.lineChartContainer.getBoundingClientRect().width -
        CHART_SVG_OFFSET_END_X,
      this.chartSVGMinWidth
    );

    this.lineChart.style.setProperty("--chart-width", `${this.chartWidth}px`);

    this.updateChartPoints();
  };

  setData = (data: StatsLineItem[]) => {
    this.chartPoints = [];

    this.data = data.map(({ gameData, ...rest }) => {
      const initialGameData = gameData
        ? {
            points: 0,
            assists: 0,
            rebounds: 0,
            steals: 0,
            blocks: 0,
          }
        : undefined;
      return { ...rest, gameData: initialGameData };
    });

    this.init({ updateYaxis: true });

    const lastDataIndex = this.data.findIndex(
      (_, index) => !this.data[index + 1]?.gameData
    );

    this.currentHoverIndex = lastDataIndex;

    if (data.length) {
      requestAnimationFrame(() => {
        this.data = data;

        this.setAxisLabels({ isUpdateXaxis: true, isUpdateYaxis: true });
        this.updateChartPoints();
      });
    }
  };

  setDataKey = (dataKey: StatsLineChartDataKey) => {
    if (this.currDataKey === dataKey) {
      return;
    }

    this.currDataKey = dataKey;

    this.setAxisLabels({ isUpdateYaxis: true });
    this.updateChartPoints();
  };

  destroy() {
    this.lineChartContainerResizeObserver.disconnect();
  }
}
